import {isTestAccount, type TestAccountInfo} from "@buildwithflux/accounts-logic";
import {isPictureFieldObject, resolveAvatarUrl} from "@buildwithflux/avatar-logic";
import {
    type SearchIndexAlias,
    TYPESENSE_PARTS_COLLECTION_NAME,
    TYPESENSE_PROJECTS_COLLECTION_NAME,
} from "@buildwithflux/constants";
import {type DocumentDataForUrlRouting, getDocumentUrl, Routes} from "@buildwithflux/url";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import List from "@mui/material/List";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {useCallback, useEffect, useState} from "react";
import {
    useSearchParams,
    useNavigate,
    useLoaderData,
    Link as RouterLink,
    type LoaderFunctionArgs,
    type MetaArgs,
    type MetaDescriptor,
} from "react-router";
import SparkMD5 from "spark-md5";
import type {Client} from "typesense";

import {useViewer} from "../context/ViewerContext";
import {useAccountsViewClient} from "../hooks/useAccountsViewClient";
import {useDocumentTitle} from "../hooks/useDocumentTitle";
import {useSearchCardUnification} from "../hooks/useSearchCardUnification";
import {useSearchListingConfig} from "../hooks/useSearchListingConfig";
import {useTypesenseClient} from "../hooks/useTypesenseClient";
import {APP_DESCRIPTION, siteMetaDescriptors, toCanonicalUrl} from "../seo";
import type {SiteSearchLoadContext} from "../server/loaderContext";
import {typesenseSearch} from "../server/typesenseRest";
import {getPreviewImageUrl} from "../utils/imageUrl";

import AccountCard, {type AccountHit} from "./cards/AccountCard";
import ProjectCard, {type ProjectHit} from "./cards/ProjectCard";
import UnifiedProjectCardSkeleton from "./cards/UnifiedProjectCardSkeleton";
import {useHideNativeScrollbar} from "./search/SearchListingScrollbar";
import VirtualizedSearchResults from "./search/VirtualizedSearchResults";

const PER_PAGE = 25;

// Size hints for the virtualized listing when the unified project cards are
// active: the card's typical rendered height and its sibling-margin spacing.
const UNIFIED_CARD_ESTIMATED_HEIGHT_PX = 210;
const UNIFIED_CARD_ROW_GAP_PX = 12;

function renderUnifiedProjectSkeleton() {
    return <UnifiedProjectCardSkeleton />;
}

type SearchRow = {kind: "account"; data: AccountHit} | {kind: "project"; data: ProjectHit};

export function meta({data, location}: MetaArgs): MetaDescriptor[] {
    const searchParams = new URLSearchParams(location.search);
    const query = searchParams.get("q") ?? "";
    const queryType = (searchParams.get("type") ?? "projects").toLowerCase() as SearchIndexAlias;
    const pageNum = parseInt(searchParams.get("page") ?? "1", 10);

    if (!query) {
        return [
            {title: "Flux Search"},
            {name: "description", content: APP_DESCRIPTION},
            {property: "og:title", content: "Flux Search"},
            {property: "og:description", content: APP_DESCRIPTION},
            {property: "og:type", content: "website"},
            {property: "og:url", content: toCanonicalUrl(Routes.SEARCH)},
            {name: "twitter:title", content: "Flux Search"},
            {name: "twitter:description", content: APP_DESCRIPTION},
            ...siteMetaDescriptors(),
        ];
    }

    const title = `Flux Search Results for "${query}"`;
    const absoluteUrl = toCanonicalUrl(`/search?q=${encodeURIComponent(query)}&type=${queryType}`);
    const loaderData = data as SearchLoaderData | null | undefined;
    const totalHits = loaderData && loaderData.query === query ? loaderData.totalHits : 0;
    const totalPages = Math.ceil(totalHits / PER_PAGE);

    const descriptors: MetaDescriptor[] = [
        {title},
        {name: "description", content: APP_DESCRIPTION},
        {tagName: "link", rel: "canonical", href: absoluteUrl},
        {property: "og:title", content: title},
        {property: "og:description", content: APP_DESCRIPTION},
        {property: "og:type", content: "website"},
        {property: "og:url", content: absoluteUrl},
        {name: "twitter:title", content: title},
        {name: "twitter:description", content: APP_DESCRIPTION},
        ...siteMetaDescriptors(),
    ];

    if (pageNum < totalPages) {
        descriptors.push({
            tagName: "link",
            rel: "next",
            href: toCanonicalUrl(`/search?q=${encodeURIComponent(query)}&page=${pageNum + 1}`),
        });
    }

    if (totalHits > 0) {
        descriptors.push({
            "script:ld+json": {
                "@context": "https://schema.org",
                "@type": "ItemList",
                name: title,
                description: `Search results for "${query}" in ${humanizeType(queryType)} on Flux`,
                url: absoluteUrl,
                numberOfItems: totalHits,
            },
        });
    }

    return descriptors;
}

function isAccountQuery(queryType: SearchIndexAlias): boolean {
    return queryType === "users" || queryType === "orgs";
}

function humanizeType(queryType: SearchIndexAlias): string {
    switch (queryType) {
        case "projects":
            return "Projects";
        case "components":
            return "Components";
        case "users":
            return "Users";
        case "orgs":
            return "Organizations";
    }
}

function getProjectCollectionName(queryType: "projects" | "components"): string {
    return queryType === "projects" ? TYPESENSE_PROJECTS_COLLECTION_NAME : TYPESENSE_PARTS_COLLECTION_NAME;
}

function getProjectQueryBy(queryType: "projects" | "components"): string {
    return queryType === "projects" ? "uid,name,description,slug" : "uid,name,nameSuffixes,description";
}

function getProjectSortBy(queryType: "projects" | "components"): string {
    return queryType === "projects"
        ? "star_count:desc,_text_match:desc,comment_count:desc"
        : "_text_match:desc,contentScore:desc,updated_at:desc";
}

function getProjectFilterBy(queryType: "projects" | "components"): string {
    return queryType === "projects" ? "test_data:false&&is_published:false" : "test_data:false";
}

function num(value: unknown): number | undefined {
    return typeof value === "number" ? value : undefined;
}

function str(value: unknown): string {
    return typeof value === "string" ? value : "";
}

function bool(value: unknown): boolean {
    return value === true;
}

function toProjectHit(doc: {[key: string]: unknown}, queryType: "projects" | "components"): ProjectHit | null {
    const uid = str(doc.uid ?? doc.objectID);
    const ownerHandle = str(doc.owner_handle ?? doc.handle);
    const slug = str(doc.slug);

    let url: string;
    const projectUrl = str(doc.project_url);
    if (projectUrl) {
        // Canonical slug~short_id URL denormalized onto the search record by the backend
        url = projectUrl;
    } else if (queryType === "projects" && uid && slug && ownerHandle) {
        // Projects collection carries slug/short_id, so build the canonical slug~short_id URL
        url = getDocumentUrl(doc as DocumentDataForUrlRouting);
    } else {
        // No canonical URL available (e.g. a part whose project_url has not been
        // backfilled). Omit the hit rather than emit a non-routable
        // /projects/<uid> link, which 404s (a part uid is not a project uid).
        return null;
    }

    const contributorUids = Array.isArray(doc.contributor_uids)
        ? (doc.contributor_uids.filter((u) => typeof u === "string") as string[])
        : [];

    return {
        uid,
        name: str(doc.name),
        description: str(doc.description),
        url,
        thumbUrl: getPreviewImageUrl(str(doc.preview_image) || undefined, 124),
        ownerUid: str(doc.owner_uid),
        ownerHandle,
        contributorUids,
        usageCount: num(doc.usage_count) ?? num(doc.use_count),
        commentCount: num(doc.comment_count),
        starCount: num(doc.star_count),
        updatedAt: num(doc.updated_at),
    };
}

const ACCOUNT_AVATAR_SIZE = 40;

/**
 * Resolves the avatar URL straight from the search hit, mirroring the main
 * app's UserStorageHelper.getUserPicture / OrganizationStorageHelper
 * precedence: PictureField object, then custom picture key (img-cdn), then
 * gravatar fallback, then legacy plain-string picture.
 */
function toHitAvatarUrl(doc: {[key: string]: unknown}, isOrganization: boolean, handle: string): string | undefined {
    const picture = doc.picture;
    if (isPictureFieldObject(picture)) {
        return resolveAvatarUrl(picture, ACCOUNT_AVATAR_SIZE);
    }
    const customKey = str(isOrganization ? doc.customPictureKey : doc.custom_picture_key);
    if (customKey) {
        return resolveAvatarUrl({type: "legacy-cdn", key: customKey}, ACCOUNT_AVATAR_SIZE);
    }
    if (!picture && handle) {
        // Same URL shape as the main app's gravatar-url fallback
        return `https://gravatar.com/avatar/${SparkMD5.hash(handle)}?size=${ACCOUNT_AVATAR_SIZE}&default=retro`;
    }
    return typeof picture === "string" && picture ? picture : undefined;
}

function toAccountHit(doc: {[key: string]: unknown}): AccountHit {
    const handle = str(doc.handle);
    const isOrganization = doc.accountType === "organization";
    // Mirror production's typed accessors so cards render identically:
    // - getAccountDisplayName: user -> full_name, org -> displayName
    // - getAccountProjectCount: user -> documents_count, org -> documentsCount
    // - getAccountFollowersCount: follower_count for both
    // Orgs carry neither displayName nor documentsCount in the projection, so
    // the name line stays empty and the project counter hides, matching prod.
    return {
        uid: str(doc.uid ?? doc.id),
        handle,
        displayName: isOrganization ? str(doc.displayName) : str(doc.full_name),
        accountType: isOrganization ? "organization" : "user",
        followerCount: num(doc.follower_count),
        projectCount: isOrganization ? num(doc.documentsCount) : num(doc.documents_count),
        verified: bool(doc.verified),
        pictureUrl: toHitAvatarUrl(doc, isOrganization, handle),
    };
}

/** Production filters test accounts at the card level with `isTestAccount("loose")`. */
function keepAccount(doc: {[key: string]: unknown}, queryType: "users" | "orgs"): boolean {
    if (queryType === "users") {
        return !isTestAccount({...doc, isAnonymous: false, is_test: false} as unknown as TestAccountInfo, "loose");
    }
    return !isTestAccount(doc as TestAccountInfo, "loose");
}

async function executeProjectSearch(
    client: Client,
    queryType: "projects" | "components",
    query: string,
    page: number,
): Promise<{rows: SearchRow[]; totalHits: number}> {
    const response = await client
        .collections(getProjectCollectionName(queryType))
        .documents()
        .search({
            q: query,
            query_by: getProjectQueryBy(queryType),
            sort_by: getProjectSortBy(queryType),
            filter_by: getProjectFilterBy(queryType),
            per_page: PER_PAGE,
            page,
            min_len_1typo: 5,
            min_len_2typo: 8,
        });

    const rows = (response.hits ?? []).flatMap<SearchRow>((hit) => {
        const data = toProjectHit(hit.document as {[key: string]: unknown}, queryType);
        return data ? [{kind: "project", data}] : [];
    });

    return {rows, totalHits: response.found};
}

async function executeAccountSearch(
    client: Client,
    collection: string,
    queryType: "users" | "orgs",
    query: string,
    page: number,
): Promise<{rows: SearchRow[]; totalHits: number}> {
    const response = await client
        .collections(collection)
        .documents()
        .search({
            q: query,
            query_by: "full_name,handle",
            sort_by: "follower_count:desc,_text_match:desc,star_count:desc",
            filter_by: queryType === "users" ? "accountType:=user" : "accountType:=organization",
            per_page: PER_PAGE,
            page,
            min_len_1typo: 4,
            min_len_2typo: 8,
        });

    const rows = (response.hits ?? [])
        .map((hit) => hit.document as {[key: string]: unknown})
        .filter((doc) => keepAccount(doc, queryType))
        .map<SearchRow>((doc) => ({kind: "account", data: toAccountHit(doc)}));

    return {rows, totalHits: response.found};
}

/**
 * Client-side counterpart of `enrichProjectVerified`: batch-resolve owner
 * `verified` flags from the accounts-view collection using the scoped
 * Typesense client, so badges render on the SPA-shell / federated paths too.
 */
async function enrichProjectVerifiedClient(rows: SearchRow[], client: Client, collection: string): Promise<void> {
    const ownerIds = Array.from(
        new Set(
            rows
                .filter((row): row is {kind: "project"; data: ProjectHit} => row.kind === "project")
                .map((row) => row.data.ownerUid)
                .filter(Boolean)
                .map((uid) => (uid.startsWith("usr_") ? uid : `usr_${uid}`)),
        ),
    );
    if (ownerIds.length === 0) return;

    try {
        const response = await client.collections(collection).documents().search({
            q: "*",
            query_by: "handle",
            filter_by: `id:[${ownerIds.join(",")}]`,
            include_fields: "id,verified",
            per_page: Math.min(ownerIds.length, 250),
        });

        const verifiedById = new Map<string, boolean>();
        for (const hit of response.hits ?? []) {
            const doc = hit.document as {[key: string]: unknown};
            const id = str(doc.id);
            if (id) verifiedById.set(id, bool(doc.verified));
        }

        for (const row of rows) {
            if (row.kind !== "project") continue;
            const id = row.data.ownerUid.startsWith("usr_") ? row.data.ownerUid : `usr_${row.data.ownerUid}`;
            row.data.ownerVerified = verifiedById.get(id) ?? false;
        }
    } catch (err) {
        // Enrichment is best-effort; never fail the whole render over a badge.
        // eslint-disable-next-line no-console
        console.error("owner verified enrichment failed:", err);
    }
}

export interface SearchLoaderData {
    query: string;
    queryType: SearchIndexAlias;
    page: number;
    rows: SearchRow[];
    totalHits: number;
}

/**
 * Batch-resolve the owner's `verified` flag for project/component hits from the
 * public-tier accounts-view projection and join it server-side, so the badge
 * renders into the SSR HTML (no client-side per-card fetch).
 */
async function enrichProjectVerified(rows: SearchRow[], context: SiteSearchLoadContext): Promise<void> {
    const accountsView = context.accountsView;
    if (!accountsView) return;

    const ownerIds = Array.from(
        new Set(
            rows
                .filter((row): row is {kind: "project"; data: ProjectHit} => row.kind === "project")
                .map((row) => row.data.ownerUid)
                .filter(Boolean)
                .map((uid) => (uid.startsWith("usr_") ? uid : `usr_${uid}`)),
        ),
    );
    if (ownerIds.length === 0) return;

    try {
        const response = await typesenseSearch(
            accountsView.apiKey,
            accountsView.collection,
            {
                q: "*",
                query_by: "handle",
                filter_by: `id:[${ownerIds.join(",")}]`,
                include_fields: "id,verified",
                per_page: Math.min(ownerIds.length, 250),
            },
            accountsView.host,
        );

        const verifiedById = new Map<string, boolean>();
        for (const hit of response.hits) {
            const id = str(hit.document.id);
            if (id) verifiedById.set(id, bool(hit.document.verified));
        }

        for (const row of rows) {
            if (row.kind !== "project") continue;
            const id = row.data.ownerUid.startsWith("usr_") ? row.data.ownerUid : `usr_${row.data.ownerUid}`;
            row.data.ownerVerified = verifiedById.get(id) ?? false;
        }
    } catch (err) {
        // Enrichment is best-effort; never fail the whole render over a badge.
        // eslint-disable-next-line no-console
        console.error("owner verified enrichment failed:", err);
    }
}

/**
 * Server loader: runs in the SSR worker at the anonymous/public tier (the only
 * tier a bot/crawler sees). Returns `null` when there is no query or the worker
 * could not provide a scoped key, in which case the component falls back to the
 * client-side fetch (the R2 SPA-shell path used for signed-in hard reloads).
 */
export async function loader({request, context}: LoaderFunctionArgs): Promise<SearchLoaderData | null> {
    const ctx = context as SiteSearchLoadContext;
    const url = new URL(request.url);
    const query = url.searchParams.get("q") ?? "";
    if (!query) return null;

    const queryType = (url.searchParams.get("type") ?? "projects").toLowerCase() as SearchIndexAlias;
    const page = parseInt(url.searchParams.get("page") ?? "1", 10) || 1;

    if (isAccountQuery(queryType)) {
        const accountsView = ctx.accountsView;
        if (!accountsView) return null;
        const response = await typesenseSearch(
            accountsView.apiKey,
            accountsView.collection,
            {
                q: query,
                query_by: "full_name,handle",
                sort_by: "follower_count:desc,_text_match:desc,star_count:desc",
                filter_by: queryType === "users" ? "accountType:=user" : "accountType:=organization",
                per_page: PER_PAGE,
                page,
                min_len_1typo: 4,
                min_len_2typo: 8,
            },
            accountsView.host,
        );
        const rows = response.hits
            .map((hit) => hit.document)
            .filter((doc) => keepAccount(doc, queryType as "users" | "orgs"))
            .map<SearchRow>((doc) => ({kind: "account", data: toAccountHit(doc)}));
        return {query, queryType, page, rows, totalHits: response.found};
    }

    const typesenseApiKey = ctx.typesenseApiKey;
    if (!typesenseApiKey) return null;
    const projectType = queryType as "projects" | "components";
    const response = await typesenseSearch(typesenseApiKey, getProjectCollectionName(projectType), {
        q: query,
        query_by: getProjectQueryBy(projectType),
        sort_by: getProjectSortBy(projectType),
        filter_by: getProjectFilterBy(projectType),
        per_page: PER_PAGE,
        page,
        min_len_1typo: 5,
        min_len_2typo: 8,
    });
    const rows = response.hits.flatMap<SearchRow>((hit) => {
        const data = toProjectHit(hit.document, projectType);
        return data ? [{kind: "project", data}] : [];
    });
    await enrichProjectVerified(rows, ctx);
    return {query, queryType, page, rows, totalHits: response.found};
}

function createSearchUrl(query: string, type?: string): string {
    const params = new URLSearchParams();
    params.set("q", query);
    if (type) params.set("type", type);
    return `${Routes.SEARCH}?${params.toString()}`;
}

export default function SearchPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const projectClient = useTypesenseClient();

    const query = searchParams.get("q") ?? "";
    const queryType = (searchParams.get("type") ?? "projects").toLowerCase() as SearchIndexAlias;
    const pageNum = parseInt(searchParams.get("page") ?? "1", 10);

    const accountQuery = isAccountQuery(queryType);
    const {uid: viewerUid} = useViewer();
    // Tier-aware accounts-view client; re-resolves when the viewer's auth changes.
    const accountsView = useAccountsViewClient(viewerUid);

    const humanizedType = humanizeType(queryType);

    // When served by the SSR worker the loader has already fetched (and
    // enriched) the results for this exact query; render them directly and skip
    // the client fetch. On the R2 SPA-shell path (signed-in hard reload, or
    // federated host) there is no loader, so we fall back to fetching here.
    const loaderData = useLoaderData() as SearchLoaderData | null | undefined;
    const ssr =
        loaderData && loaderData.query === query && loaderData.queryType === queryType && loaderData.page === pageNum
            ? loaderData
            : null;

    const [clientRows, setClientRows] = useState<SearchRow[]>([]);
    const [clientTotalHits, setClientTotalHits] = useState(0);
    const [loading, setLoading] = useState(false);

    const rows = ssr ? ssr.rows : clientRows;
    const totalHits = ssr ? ssr.totalHits : clientTotalHits;

    // Flag-gated virtualized listing (client-side only): spans the full result
    // set with on-demand page fetching instead of Previous/Next pagination.
    const {virtualization, customScrollbar} = useSearchListingConfig();
    const cardUnification = useSearchCardUnification();

    // Suppress the native scrollbar as soon as the flag resolves — before the
    // results (and the custom track) finish rendering — so the viewport width
    // doesn't jump mid-load when the track mounts.
    useHideNativeScrollbar(virtualization && customScrollbar);

    /** Fetches one page of results for the virtualized listing. */
    const fetchPage = useCallback(
        async (page: number): Promise<readonly SearchRow[]> => {
            if (accountQuery) {
                if (!accountsView) throw new Error("accounts search client unavailable");
                const data = await executeAccountSearch(
                    accountsView.client,
                    accountsView.collection,
                    queryType as "users" | "orgs",
                    query,
                    page,
                );
                return data.rows;
            }
            if (!projectClient) throw new Error("project search client unavailable");
            const data = await executeProjectSearch(
                projectClient,
                queryType as "projects" | "components",
                query,
                page,
            );
            if (accountsView) {
                await enrichProjectVerifiedClient(data.rows, accountsView.client, accountsView.collection);
            }
            return data.rows;
        },
        [accountQuery, accountsView, projectClient, query, queryType],
    );

    const renderRow = useCallback(
        (row: SearchRow) =>
            row.kind === "account" ? (
                <AccountCard key={row.data.uid} account={row.data} />
            ) : (
                <ProjectCard key={row.data.uid} project={row.data} />
            ),
        [],
    );

    const loadResults = useCallback(async () => {
        if (!query || ssr) return;

        if (accountQuery) {
            if (!accountsView) return;
            setLoading(true);
            try {
                const data = await executeAccountSearch(
                    accountsView.client,
                    accountsView.collection,
                    queryType as "users" | "orgs",
                    query,
                    pageNum,
                );
                setClientRows(data.rows);
                setClientTotalHits(data.totalHits);
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error("Search failed:", err);
            } finally {
                setLoading(false);
            }
            return;
        }

        if (!projectClient) return;
        setLoading(true);
        try {
            const data = await executeProjectSearch(
                projectClient,
                queryType as "projects" | "components",
                query,
                pageNum,
            );
            if (accountsView) {
                await enrichProjectVerifiedClient(data.rows, accountsView.client, accountsView.collection);
            }
            setClientRows(data.rows);
            setClientTotalHits(data.totalHits);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error("Search failed:", err);
        } finally {
            setLoading(false);
        }
    }, [ssr, accountQuery, accountsView, projectClient, query, queryType, pageNum]);

    useEffect(() => {
        void loadResults();
    }, [loadResults]);

    const handleSearchSubmit = useCallback(
        (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            const formData = new FormData(event.currentTarget);
            const newQuery = formData.get("query") as string;
            if (newQuery) {
                void navigate(createSearchUrl(newQuery, queryType));
            }
        },
        [navigate, queryType],
    );

    const totalPages = Math.ceil(totalHits / PER_PAGE);

    useDocumentTitle(query ? `Flux Search Results for "${query}"` : "Flux Search");

    if (!query) {
        return (
            <>
                <Container maxWidth="md" sx={{py: 8, textAlign: "center"}}>
                    <Typography variant="h3" component="h1" sx={{mb: 4}}>
                        Search Flux
                    </Typography>
                    <Box component="form" onSubmit={handleSearchSubmit} sx={{maxWidth: 500, mx: "auto"}}>
                        <TextField
                            name="query"
                            placeholder="Search Flux"
                            fullWidth
                            autoFocus
                            variant="outlined"
                            sx={{bgcolor: "rgba(33,33,33,0.8)"}}
                        />
                    </Box>
                </Container>
            </>
        );
    }

    const absoluteUrl = toCanonicalUrl(`/search?q=${encodeURIComponent(query)}&type=${queryType}`);

    return (
        <>
            <Container maxWidth="md">
                <Box sx={{mb: 2}}>
                    <Breadcrumbs aria-label="Breadcrumb">
                        <Link component={RouterLink} to={Routes.SEARCH} color="inherit" underline="hover">
                            Search
                        </Link>
                        <Link href={absoluteUrl} color="inherit" underline="hover">
                            &quot;{query}&quot; in {humanizedType}
                            {totalHits > 0 && ` (${totalHits} results)`}
                        </Link>
                    </Breadcrumbs>
                </Box>

                <Grid container spacing={2}>
                    <Grid component="aside" size={{xs: 12, lg: 3}}>
                        <Paper square>
                            <Box sx={{p: 1.5}}>
                                <Typography variant="body1" sx={{mb: 1}}>
                                    Filter by
                                </Typography>
                                <List disablePadding>
                                    {(["projects", "components", "users", "orgs"] as const).map((type) => (
                                        <li key={type}>
                                            <Link
                                                component={RouterLink}
                                                to={createSearchUrl(query, type)}
                                                color={queryType === type ? "primary" : "text.secondary"}
                                                underline="hover"
                                                variant="body2"
                                            >
                                                {humanizeType(type)}
                                            </Link>
                                        </li>
                                    ))}
                                </List>
                            </Box>
                        </Paper>
                    </Grid>

                    <Grid component="main" size={{xs: 12, lg: 9}} aria-live="polite">
                        {loading ? (
                            <Box sx={{display: "flex", justifyContent: "center", py: 8}}>
                                <CircularProgress />
                            </Box>
                        ) : virtualization && totalHits > 0 ? (
                            <VirtualizedSearchResults
                                key={`${queryType}:${query}`}
                                totalHits={totalHits}
                                perPage={PER_PAGE}
                                initialPage={pageNum}
                                initialRows={rows}
                                fetchPage={fetchPage}
                                renderRow={renderRow}
                                renderSkeleton={
                                    !accountQuery && cardUnification ? renderUnifiedProjectSkeleton : undefined
                                }
                                estimatedRowHeightPx={
                                    !accountQuery && cardUnification ? UNIFIED_CARD_ESTIMATED_HEIGHT_PX : undefined
                                }
                                rowGapPx={!accountQuery && cardUnification ? UNIFIED_CARD_ROW_GAP_PX : undefined}
                                customScrollbar={customScrollbar}
                            />
                        ) : (
                            <>
                                <List aria-label={humanizedType} disablePadding>
                                    {rows.map((row) =>
                                        row.kind === "account" ? (
                                            <AccountCard key={row.data.uid} account={row.data} />
                                        ) : (
                                            <ProjectCard key={row.data.uid} project={row.data} />
                                        ),
                                    )}
                                </List>

                                {rows.length === 0 && (
                                    <Box sx={{p: 1.5}} role="status">
                                        <Typography variant="h6" component="h2">
                                            No results found for &quot;{query}&quot;
                                        </Typography>
                                    </Box>
                                )}

                                {totalPages > 1 && (
                                    <Box sx={{display: "flex", justifyContent: "center", gap: 2, py: 4}}>
                                        {pageNum > 1 && (
                                            <Link
                                                component={RouterLink}
                                                to={`${Routes.SEARCH}?q=${encodeURIComponent(query)}&type=${queryType}&page=${pageNum - 1}`}
                                            >
                                                Previous
                                            </Link>
                                        )}
                                        <Typography color="text.secondary" variant="body2">
                                            Page {pageNum} of {totalPages}
                                        </Typography>
                                        {pageNum < totalPages && (
                                            <Link
                                                component={RouterLink}
                                                to={`${Routes.SEARCH}?q=${encodeURIComponent(query)}&type=${queryType}&page=${pageNum + 1}`}
                                            >
                                                Next
                                            </Link>
                                        )}
                                    </Box>
                                )}
                            </>
                        )}
                    </Grid>
                </Grid>
            </Container>
        </>
    );
}
